import React from 'react';
import { motion, Variants } from 'framer-motion';
import { Check, Mail, MessageSquare, ArrowRight } from 'lucide-react';

const BUTTON_ITEMS = [
  "Automated Climate & AHU Setup",
  "Short Method Compost (Phase I & II)",
  "Casing Chemistry & Watering Tech",
  "Pest, Hygiene & Disease Control",
  "Cold Chain & Processing Basics",
  "CMEGP Subsidies & Project Reports"
];

const OYSTER_ITEMS = [
  "Commercial Straw Treatment",
  "Steam Pasteurization Frameworks",
  "Spawning & Incubation Control",
  "Dehydration & Drying Matrices",
  "B2B Buyback Network Protocol",
  "Low-Cost Infrastructure Blueprint"
];

// Explicitly typing the animation configuration to satisfy TypeScript rules
const cardAnimationVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const Training2: React.FC = () => {
  return (
    <div className="bg-gray-50 pb-20">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* Main Programs Display */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          
          {/* Button Mushroom Card */}
          <motion.div 
            className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col group hover:shadow-xl transition-all duration-300"
            variants={cardAnimationVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="relative h-56 overflow-hidden bg-gray-100">
              <img src="/images/button_mushrooms.webp" alt="Button Mushroom" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute top-4 right-4 bg-blue-600 text-white text-xs font-bold tracking-wider px-3 py-1 rounded-md shadow-sm">
                60 Days Plant Access Included
              </div>
            </div>
            <div className="p-6 md:p-8 flex-grow flex flex-col">
              <h3 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-2">Button Mushroom Program</h3>
              <p className="text-xs font-bold uppercase text-blue-600 tracking-wider mb-6">Offline Practical Block • Advanced Cultivation</p>
              
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 flex-grow">
                {BUTTON_ITEMS.map((detail, index) => (
                  <li key={index} className="flex items-center text-xs md:text-sm text-gray-600 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <Check className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                    <span className="font-medium">{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Oyster Mushroom Card */}
          <motion.div 
            className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col group hover:shadow-xl transition-all duration-300"
            variants={cardAnimationVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="relative h-56 overflow-hidden bg-gray-100">
              <img src="/images/oyster_mushrooms.webp" alt="Oyster Mushroom" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute top-4 right-4 bg-green-600 text-white text-xs font-bold tracking-wider px-3 py-1 rounded-md shadow-sm">
                Free Operations Kit Included
              </div>
            </div>
            <div className="p-6 md:p-8 flex-grow flex flex-col">
              <h3 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-2">Oyster Mushroom Program</h3>
              <p className="text-xs font-bold uppercase text-green-600 tracking-wider mb-6">Offline Practical Block • Direct B2B Buyback</p>
              
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 flex-grow">
                {OYSTER_ITEMS.map((detail, index) => (
                  <li key={index} className="flex items-center text-xs md:text-sm text-gray-600 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    <span className="font-medium">{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Mini Tracks Rows */}
        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          <motion.div 
            className="bg-white p-4 rounded-xl border border-gray-200 flex items-center justify-between"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center space-x-3">
              <span className="w-3 h-3 rounded-full bg-amber-500 shadow-sm animate-pulse"></span>
              <span className="text-sm font-bold text-gray-800">Milky Mushroom Technical Track</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
          </motion.div>

          <motion.div 
            className="bg-white p-4 rounded-xl border border-gray-200 flex items-center justify-between"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center space-x-3">
              <span className="w-3 h-3 rounded-full bg-red-500 shadow-sm animate-pulse"></span>
              <span className="text-sm font-bold text-gray-800">Cordyceps Militaris Laboratory Specialization</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
          </motion.div>
        </div>

        {/* Clean, Fast CTA Grid */}
        <motion.div 
          className="bg-gray-900 rounded-2xl p-8 text-center text-white relative overflow-hidden max-w-3xl mx-auto"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-2xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl pointer-events-none"></div>

          <h3 className="text-2xl font-black mb-2">Secure Your Seat</h3>
          <p className="text-gray-400 text-xs md:text-sm mb-6 max-w-md mx-auto">Limited batch accommodations for physical practical access. Connect directly to register.</p>
          
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3">
            <a 
              href="mailto:enquiry@wardhamushroom.com?subject=Enrollment%20Inquiry%20for%20Commercial%20Mushroom%20Training" 
              className="w-full sm:w-auto flex items-center justify-center bg-white text-gray-950 px-6 py-3 rounded-xl font-bold text-sm hover:bg-gray-100 transition-all gap-2"
            >
              <Mail className="w-4 h-4" /> Mail to Enroll
            </a>
            <a 
              href="https://wa.me/917263071957" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="w-full sm:w-auto flex items-center justify-center bg-green-600 text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-green-500 transition-all gap-2"
            >
              <MessageSquare className="w-4 h-4" /> WhatsApp Admissions
            </a>
          </div>
          
          <p className="text-[10px] text-gray-500 mt-6 tracking-wide uppercase">Operations: Wardha, Maharashtra, India • www.wardhamushroom.com</p>
        </motion.div>

      </div>
    </div>
  );
};

export default Training2;